<script setup>
import ContactSupportDialog from "@/components/dialogs/ContactSupportDialog.vue";

import axios from "axios";
import { ref, reactive, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { Checkbox, useRecaptchaProvider } from "vue-recaptcha";
import { useRouter, useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { useStore } from "vuex";

import useGTM from "@/utils/gtm";
import { validateEmail, validateName } from "@/utils/validation";

useRecaptchaProvider();

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// Loading state for guest login to prevent UI shift
const isGuestLoginInProgress = ref(false);

// Check if user is already logged in as guest
const isGuest = computed(() => store.state.auth.isLoggedIn && store.state.auth.user.isTemporary);

// Modified isGuest for UI display to prevent shifting during login
const displayAsGuest = computed(() => isGuest.value && !isGuestLoginInProgress.value);

// Guest form data
const guestFormData = reactive({
  terms_accepted: false,
});

// Mobile detection for inline style override
const isMobile = computed(() => {
  return window.innerWidth <= 768;
});

// Single column layout detection (for separator visibility)
const isSingleColumn = computed(() => {
  return window.innerWidth <= 1100;
});

const guestSectionStyle = computed(() => {
  if (isMobile.value) {
    return {
      position: "static",
      display: "block",
      flex: "none",
      width: "100%",
      height: "auto",
      padding: "16px 20px",
      margin: "0",
      maxWidth: "none",
      minWidth: "auto",
      flexBasis: "auto",
      backgroundColor: "transparent",
      textAlign: "center",
    };
  }
  return {};
});

const showPassword = ref(false);
const showConfirmPassword = ref(false);
const recaptchaAttempt = ref(0);
const apiUrl = import.meta.env.VITE_APP_API_BASE_URL;

const formData = reactive({
  email: "",
  name: "",
  password: "",
  password_confirmation: "",
  google_recaptcha_response: "",
  terms_consent: false,
  marketing_consent: false,
});

const errors = reactive({
  email: "",
  name: "",
  password: "",
  password_confirmation: "",
  recaptcha: "",
});

const generalError = ref("");
const isContactSupportDialogOpen = ref(false);

const isLoading = reactive({
  email: false,
  google: false,
  microsoft: false,
  apple: false,
});

const isFormValid = computed(() => {
  return (
    formData.email &&
    formData.name &&
    formData.password &&
    formData.password_confirmation &&
    formData.google_recaptcha_response &&
    formData.terms_consent &&
    !errors.email &&
    !errors.name &&
    !errors.password &&
    !errors.password_confirmation
  );
});

const clearError = (field) => {
  errors[field] = "";
  // Clear general error when user starts typing
  if (generalError.value) {
    generalError.value = "";
  }

  // If password is cleared, also clear confirm password
  if (field === "password" && !formData.password) {
    formData.password_confirmation = "";
    errors.password_confirmation = "";
  }
};

// Custom localized validation for password
const validatePasswordLocalized = (password) => {
  if (!password) {
    return t("validation.field_required");
  }
  if (password.length < 8) {
    return t("validation.password_min_length");
  }
  if (!/\d/.test(password)) {
    return t("validation.password_digit");
  }
  if (!/[A-Z]/.test(password)) {
    return t("validation.password_uppercase");
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return t("validation.password_special");
  }
  return "";
};

const validateForm = () => {
  errors.email = validateEmail(formData.email);
  errors.name = validateName(formData.name);

  // Use localized password validation
  errors.password = validatePasswordLocalized(formData.password);

  // Handle password confirmation with proper translation
  errors.password_confirmation = "";
  if (
    formData.password &&
    formData.password_confirmation &&
    formData.password !== formData.password_confirmation
  ) {
    errors.password_confirmation = t("auth.password_mismatch");
  }

  errors.recaptcha = formData.google_recaptcha_response ? "" : t("auth.recaptcha");

  return (
    !errors.email &&
    !errors.name &&
    !errors.password &&
    !errors.password_confirmation &&
    !errors.recaptcha
  );
};

const handleGTM = (method) => {
  useGTM.pushEvent({
    event: "registration",
    loginMethod: method,
  });
};

const login = async (token) => {
  store.commit("auth/logout");
  store.commit("auth/login", token);
  await router.push("/documents");
};

const handleSocialLogin = async (provider) => {
  useToast().clear();
  isLoading[provider] = true;

  try {
    store.commit("auth/logout");
    window.location.href = apiUrl + "/login/" + provider;
  } catch (error) {
    useToast().error(t("notifications.default_error"));
    isLoading[provider] = false;
  }
};

const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  isLoading.email = true;

  try {
    const response = await axios.post("api/register", {
      email: formData.email,
      name: formData.name,
      password: formData.password,
      password_confirmation: formData.password_confirmation,
      google_recaptcha_response: formData.google_recaptcha_response,
      terms_consent: formData.terms_consent,
      marketing_consent: formData.marketing_consent,
    });

    if (response.status === 201) {
      useToast().success(t("notifications.register_success"));
      handleGTM("email");
      await login(response.data.token);
    }
  } catch (error) {
    useToast().clear();

    // Check if it's a "User already exists" error
    if (error.response?.status === 422 || error.response?.data?.error === "User already exists") {
      generalError.value = t("validation.user_already_exists");
    } else {
      generalError.value = t("notifications.default_error");
    }

    // Reset reCAPTCHA and clear password for security
    formData.google_recaptcha_response = "";
    formData.password = "";
    formData.password_confirmation = "";
    recaptchaAttempt.value++;
  } finally {
    isLoading.email = false;
  }
};

const handleGuestLogin = async () => {
  try {
    isGuestLoginInProgress.value = true;
    const response = await axios.post("/api/temporary-user");
    store.commit("auth/login", response.data.token);

    // Wait a tick for the store and cookies to be fully set
    await new Promise((resolve) => setTimeout(resolve, 0));

    // Check for redirect parameter in URL
    const redirectTo = route.query.redirect || "/";
    console.log("🎭 Guest login successful, redirecting to:", redirectTo);
    await router.push(redirectTo);
  } catch (error) {
    console.error("Error creating guest user:", error);
    useToast().error(t("notifications.default_error"));
    isGuestLoginInProgress.value = false;
  }
};

const handleLogoClick = () => {
  if (isGuest.value) {
    router.push("/");
  }
};

const downloadTermsOfService = () => {
  const language = import.meta.env.VITE_APP_LOCALE === "pl" ? "pl" : "eng";
  const filename = `terms-of-service-${language}.pdf`;
  const link = document.createElement("a");
  link.href = `/documents/${filename}`;
  link.download = filename;
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadPrivacyPolicy = () => {
  const language = import.meta.env.VITE_APP_LOCALE === "pl" ? "pl" : "eng";
  const filename = `privacy-policy-${language}.pdf`;
  const link = document.createElement("a");
  link.href = `/documents/${filename}`;
  link.download = filename;
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const showContactSupportDialog = () => {
  isContactSupportDialogOpen.value = true;
};

onMounted(() => {
  const method = route.query.method;
  if (method) {
    localStorage.setItem("registerFlowType", method);
  }

  const emailParam = route.query.email;
  if (typeof emailParam === "string") {
    formData.email = emailParam;
  }
});
</script>

<template>
  <div class="auth-container">
    <!-- Logo Section -->
    <div class="logo-section">
      <img
        src="/images/logos/legalTorchLogo.svg"
        alt="Legal Torch Logo"
        class="logo"
        :class="{ clickable: displayAsGuest }"
        @click="handleLogoClick"
      />
    </div>

    <!-- Tab Navigation -->
    <div class="tab-navigation">
      <div class="tabs-container">
        <div class="tab active">
          {{ $t("auth.sign_up") }}
        </div>
        <router-link to="/login" class="tab">
          {{ $t("auth.sign_in") }}
        </router-link>
        <div class="tab-underline register-active"></div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="content-wrapper" :class="{ centered: displayAsGuest }">
      <div class="main-content">
        <h2 class="form-title">{{ $t("auth.sign_up") }}</h2>
        <p class="form-subtitle">
          <i18n-t keypath="auth.sign_up_promo">
            <template #freeText>
              <strong class="free-text">{{ $t("auth.sign_up_free") }}</strong>
            </template>
          </i18n-t>
        </p>

        <!-- Email Input -->
        <div class="input-group">
          <label class="input-label">{{ $t("common.email") }}*</label>
          <input
            v-model="formData.email"
            type="email"
            class="form-input"
            :placeholder="$t('common.email')"
            :class="{ error: errors.email }"
            @input="clearError('email')"
          />
          <span v-if="errors.email" class="error-text">{{ errors.email }}</span>
        </div>

        <!-- Name Input -->
        <div class="input-group">
          <label class="input-label">{{ $t("common.name") }}*</label>
          <input
            v-model="formData.name"
            type="text"
            class="form-input"
            :placeholder="$t('common.name')"
            :class="{ error: errors.name }"
            @input="clearError('name')"
          />
          <span v-if="errors.name" class="error-text">{{ errors.name }}</span>
        </div>

        <!-- Password Input -->
        <div class="input-group">
          <label class="input-label">{{ $t("auth.password") }}*</label>
          <div class="password-container">
            <input
              v-model="formData.password"
              :type="showPassword ? 'text' : 'password'"
              class="form-input"
              :placeholder="$t('auth.password')"
              :class="{ error: errors.password }"
              @input="clearError('password')"
            />
            <button type="button" class="password-toggle" @click="showPassword = !showPassword">
              <img
                v-if="!showPassword"
                src="/images/icons/eye.svg"
                alt="Show password"
                class="eye-icon"
              />
              <img v-else src="/images/icons/eye-closed.svg" alt="Hide password" class="eye-icon" />
            </button>
          </div>
          <span v-if="errors.password" class="error-text">{{ errors.password }}</span>
        </div>

        <!-- Confirm Password Input (only show when password field has content) -->
        <div v-if="formData.password" class="input-group">
          <label class="input-label">{{ $t("auth.confirm_password") }}*</label>
          <div class="password-container">
            <input
              v-model="formData.password_confirmation"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="form-input"
              :placeholder="$t('auth.confirm_password')"
              :class="{ error: errors.password_confirmation }"
              @input="clearError('password_confirmation')"
            />
            <button
              type="button"
              class="password-toggle"
              @click="showConfirmPassword = !showConfirmPassword"
            >
              <img
                v-if="!showConfirmPassword"
                src="/images/icons/eye.svg"
                alt="Show password"
                class="eye-icon"
              />
              <img v-else src="/images/icons/eye-closed.svg" alt="Hide password" class="eye-icon" />
            </button>
          </div>
          <span v-if="errors.password_confirmation" class="error-text">{{
            errors.password_confirmation
          }}</span>
        </div>

        <!-- Recaptcha -->
        <div class="recaptcha-container">
          <Checkbox :key="recaptchaAttempt" v-model="formData.google_recaptcha_response" />
          <span v-if="errors.recaptcha" class="error-text">{{ $t("auth.recaptcha") }}</span>
        </div>

        <!-- Terms and Privacy Checkboxes -->
        <div class="checkbox-group">
          <div class="checkbox-item">
            <input
              v-model="formData.terms_consent"
              type="checkbox"
              id="terms"
              class="checkbox-input"
              required
            />
            <label for="terms" class="checkbox-label">
              {{ $t("auth.accept_terms_to_continue") }}
              <span class="download-link" @click="downloadTermsOfService">{{
                $t("auth.terms_and_conditions")
              }}</span>
              {{ $t("auth.and_the") }}
              <span class="download-link" @click="downloadPrivacyPolicy">{{
                $t("auth.privacy_policy")
              }}</span
              >.
            </label>
          </div>

          <div class="checkbox-item">
            <input
              v-model="formData.marketing_consent"
              type="checkbox"
              id="marketing"
              class="checkbox-input"
            />
            <label for="marketing" class="checkbox-label">{{ t("marketing_consent") }}</label>
          </div>
        </div>

        <!-- General Error Message -->
        <div v-if="generalError" class="general-error">
          <div class="error-icon">⚠️</div>
          <span>{{ generalError }}</span>
        </div>

        <!-- Submit Button -->
        <button
          type="button"
          class="submit-btn"
          :disabled="!isFormValid"
          :class="{ loading: isLoading.email }"
          @click="handleSubmit"
        >
          <div v-if="isLoading.email" class="loading-content">
            <div class="spinner"></div>
          </div>
          <span v-else>{{ $t("auth.continue") }}</span>
        </button>

        <!-- Divider -->
        <div class="divider">
          <span class="divider-line"></span>
          <span class="divider-text">{{ $t("auth.or_use") }}</span>
        </div>

        <!-- Social Login Buttons -->
        <div class="social-buttons">
          <button
            class="social-btn google"
            :disabled="isLoading.google"
            @click="handleSocialLogin('google')"
            :title="$t('auth.register_with_google')"
          >
            <img src="/src/assets/icons/google.svg" alt="Google" class="social-icon" />
          </button>

          <button
            class="social-btn microsoft"
            :disabled="isLoading.microsoft"
            @click="handleSocialLogin('microsoft')"
            :title="$t('auth.register_with_microsoft')"
          >
            <img src="/images/logos/microsoft-icon.svg" alt="Microsoft" class="social-icon" />
          </button>

          <button
            class="social-btn apple"
            :disabled="isLoading.apple"
            @click="handleSocialLogin('apple')"
            :title="$t('auth.register_with_apple')"
          >
            <img src="/src/assets/icons/apple.svg" alt="Apple" class="social-icon" />
          </button>
        </div>

        <!-- Bottom Links -->
        <div class="bottom-links">
          <div class="bottom-link">
            {{ $t("auth.already_have_account") }}
            <router-link to="/login" class="link">{{ $t("auth.sign_in") }}</router-link>
          </div>
          <div class="help-link">
            <button type="button" class="support-link" @click="showContactSupportDialog">
              {{ $t("auth.need_help") }}
            </button>
          </div>
        </div>
      </div>

      <!-- Desktop Guest Login Section -->
      <div v-if="!displayAsGuest && !isMobile" class="guest-section">
        <!-- Separator for single column layout -->
        <div v-if="isSingleColumn" class="guest-section-separator">
          <div class="divider">
            <span class="divider-line"></span>
          </div>
        </div>
        <h3 class="guest-title">{{ $t("auth.no_account_yet") }}</h3>

        <button
          type="button"
          class="guest-btn"
          :disabled="!guestFormData.terms_accepted"
          @click="handleGuestLogin"
        >
          {{ $t("auth.try_as_guest") }}
        </button>

        <div class="guest-checkbox">
          <input
            v-model="guestFormData.terms_accepted"
            type="checkbox"
            id="guest-terms"
            class="checkbox-input"
          />
          <label for="guest-terms" class="checkbox-label">
            {{ $t("auth.accept_terms_as_guest") }}
            <span class="download-link" @click="downloadTermsOfService">{{
              $t("auth.terms_and_conditions")
            }}</span>
            {{ $t("auth.and_the") }}
            <span class="download-link" @click="downloadPrivacyPolicy">{{
              $t("auth.privacy_policy")
            }}</span
            >.
          </label>
        </div>
      </div>
    </div>

    <!-- Mobile Guest Login Section (separate from flex layout) -->
    <div v-if="!displayAsGuest && isMobile" class="mobile-guest-section">
      <div class="mobile-divider">
        <div class="divider">
          <span class="divider-line"></span>
        </div>
      </div>
      <h3 class="mobile-guest-title">{{ $t("auth.no_account_yet") }}</h3>

      <button
        type="button"
        class="mobile-guest-btn"
        :disabled="!guestFormData.terms_accepted"
        @click="handleGuestLogin"
      >
        {{ $t("auth.try_as_guest") }}
      </button>

      <div class="mobile-guest-checkbox">
        <input
          v-model="guestFormData.terms_accepted"
          type="checkbox"
          id="mobile-guest-terms"
          class="checkbox-input"
        />
        <label for="mobile-guest-terms" class="checkbox-label">
          {{ $t("auth.accept_terms_as_guest") }}
          <span class="download-link" @click="downloadTermsOfService">{{
            $t("auth.terms_and_conditions")
          }}</span>
          {{ $t("auth.and_the") }}
          <span class="download-link" @click="downloadPrivacyPolicy">{{
            $t("auth.privacy_policy")
          }}</span
          >.
        </label>
      </div>
    </div>

    <ContactSupportDialog
      :is-dialog-open="isContactSupportDialogOpen"
      @close-dialog="isContactSupportDialogOpen = false"
    />
  </div>
</template>

<style scoped lang="scss">
.auth-container {
  min-height: 100vh;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.logo-section {
  margin-bottom: 16px;
}

.logo {
  width: 256px;
  height: 23px;

  &.clickable {
    cursor: pointer;

    &:hover {
      opacity: 0.8;
      transition: opacity 0.2s ease;
    }
  }
}

.tab-navigation {
  margin-bottom: 20px;
  width: 100%;
  display: flex;
  justify-content: center;
}

.tabs-container {
  position: relative;
  display: flex;
  max-width: 300px;
  width: 100%;
}

.tab {
  flex: 1;
  padding: 12px 24px;
  text-align: center;
  text-decoration: none;
  color: #374151;
  font-weight: 500;
  transition: color 0.3s ease;
  position: relative;
  background: none;
  border: none;
  cursor: pointer;

  &.active {
    color: #2563eb;
  }

  &:hover {
    color: #2563eb;
  }
}

@media (max-width: 360px) {
  .tab {
    font-size: 14px;
  }
}

.tab-underline {
  position: absolute;
  bottom: 0;
  height: 2px;
  background-color: #2563eb;
  transition: transform 0.3s ease;
  width: 50%;

  &.register-active {
    transform: translateX(0%);
  }
}

.content-wrapper {
  width: 100%;
  max-width: 950px;
  display: flex;
  gap: 60px;
  align-items: center;

  &.centered {
    justify-content: center;
    max-width: 430px;
  }
}

.main-content {
  flex: 1;
  max-width: 460px;
}

.form-title {
  text-align: center;
  font-size: 48px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
}

.form-subtitle {
  text-align: center;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 20px;
  line-height: 1.4;

  .free-text {
    font-weight: 700;
    color: #6b7280;
  }
}

.input-group {
  margin-bottom: 16px;
}

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 16px 16px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  font-size: 16px;
  transition: border-color 0.2s;
  height: 48px;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: none;
  }

  &.error {
    border-color: #ef4444;
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.password-container {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.eye-icon {
  width: 20px;
  height: 20px;
}

.error-text {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.recaptcha-container {
  margin-bottom: 20px;
}

.general-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin-bottom: 16px;
  color: #dc2626;
  font-size: 14px;

  .error-icon {
    font-size: 16px;
    flex-shrink: 0;
  }
}

.submit-btn {
  width: 100%;
  background-color: #d8ed84;
  color: #1f2937;
  border: none;
  border-radius: 16px;
  padding: 16px 14px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-bottom: 20px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: #c9e16f;
  }

  &:disabled {
    background-color: rgba(216, 237, 132, 0.4);
    color: rgba(31, 41, 55, 0.6);
    cursor: not-allowed;
    box-shadow: 0 0 0 1px rgba(216, 237, 132, 0.2);
  }

  &.loading {
    background-color: rgba(216, 237, 132, 0.4);
    color: rgba(31, 41, 55, 0.6);
  }

  .loading-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid transparent;
    border-top: 2px solid #9ca3af;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    flex-shrink: 0;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.divider {
  margin: 20px 0;
  position: relative;
  text-align: center;
}

.divider-line {
  display: block;
  height: 1px;
  background-color: #e5e7eb;
  width: 100%;
}

.divider-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  padding: 0 16px;
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.social-buttons {
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-bottom: 20px;
  justify-content: center;
}

.social-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  width: 48px;
  height: 48px;

  &:hover:not(:disabled) {
    background-color: #f9fafb;
    border-color: #9ca3af;
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.google:hover:not(:disabled) {
    background-color: #fef2f2;
    border-color: #fca5a5;
  }

  &.microsoft:hover:not(:disabled) {
    background-color: #eff6ff;
    border-color: #93c5fd;
  }

  &.apple:hover:not(:disabled) {
    background-color: #f9fafb;
    border-color: #6b7280;
  }
}

.social-icon {
  width: 24px;
  height: 24px;
}

.bottom-links {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bottom-link {
  text-align: center;
  font-size: 14px;
  color: #6b7280;

  .link {
    color: #2563eb;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
}

.help-link {
  text-align: center;
}

.support-link {
  background: none;
  border: none;
  color: #2563eb;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
}

.guest-section {
  flex: 0 0 150px;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
  width: 350px;
}

.guest-title {
  font-size: 22px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 20px;
  white-space: nowrap;
  text-align: center;
  width: 100%;
  line-height: 1.2;
}

.guest-btn {
  background-color: #d8ed84;
  color: #1f2937;
  border: none;
  border-radius: 16px;
  padding: 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 16px;

  &:hover:not(:disabled) {
    background-color: #c9e16f;
  }

  &:disabled {
    background-color: rgba(216, 237, 132, 0.4);
    color: rgba(31, 41, 55, 0.6);
    cursor: not-allowed;
    box-shadow: 0 0 0 1px rgba(216, 237, 132, 0.2);
  }
}

.guest-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  text-align: left;
  width: 100%;

  .checkbox-input {
    margin-top: 2px;
    flex-shrink: 0;
  }

  .checkbox-label {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.4;
    cursor: pointer;
  }
}

// Handle text wrapping by moving guest section below social buttons on smaller widths
@media (max-width: 1100px) {
  .content-wrapper:not(.centered) {
    flex-direction: column;
    gap: 20px;
    align-items: center;
  }

  .guest-section {
    order: 1;
  }

  .main-content {
    order: 0;
  }
}

.checkbox-group {
  margin-bottom: 16px;
}

.checkbox-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
}

.checkbox-input {
  margin-right: 8px;
  margin-top: 2px;
  flex-shrink: 0;
}

.checkbox-label {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  cursor: pointer;

  :deep(a) {
    color: #2563eb;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .download-link {
    color: #2563eb;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
}

.guest-section-separator {
  width: 430px;
  margin-left: calc((420px - 430px) / 2);
  margin-bottom: 20px;
}

.mobile-divider {
  width: 100%;
  margin-bottom: 20px;
}

.mobile-guest-section {
  width: 100%;
  padding: 0 0px 16px 0px;
  margin: 0;
  text-align: center;
  background: transparent;
}

.mobile-guest-title {
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  margin: 0 0 12px 0;
  line-height: 1.2;
}

.mobile-guest-btn {
  width: 100%;
  max-width: 300px;
  background-color: #d8ed84;
  color: #1f2937;
  border: none;
  border-radius: 16px;
  padding: 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px auto;

  &:hover:not(:disabled) {
    background-color: #c9e16f;
  }

  &:disabled {
    background-color: rgba(216, 237, 132, 0.4);
    color: rgba(31, 41, 55, 0.6);
    cursor: not-allowed;
    box-shadow: 0 0 0 1px rgba(216, 237, 132, 0.2);
  }
}

.mobile-guest-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  text-align: left;
  justify-content: center;
  max-width: 300px;
  margin: 0 auto;

  .checkbox-input {
    margin-top: 2px;
    flex-shrink: 0;
  }

  .checkbox-label {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.4;
    cursor: pointer;
  }
}

// Mobile guest section override with maximum specificity
@media (max-width: 768px) {
  .auth-container div.content-wrapper div.guest-section {
    flex: none !important;
    flex-basis: auto !important;
    flex-grow: 0 !important;
    flex-shrink: 0 !important;
    width: auto !important;
    max-width: none !important;
    min-width: auto !important;
    height: auto !important;
    min-height: auto !important;
    max-height: none !important;
    margin: 0 !important;
    padding: 10px 0 !important;
  }

  .auth-container div.content-wrapper div.guest-section .guest-title {
    margin: 0 0 8px 0 !important;
  }

  .auth-container div.content-wrapper div.guest-section .guest-btn {
    margin: 0 0 8px 0 !important;
  }

  .auth-container div.content-wrapper div.guest-section .guest-checkbox {
    margin: 0 !important;
  }
}

@media (max-width: 768px) {
  .auth-container {
    padding: 24px 16px;
  }

  .logo {
    width: 200px;
    height: 18px;
  }

  .tabs-container {
    min-width: auto;
    width: 100%;
    max-width: 300px;
  }

  .content-wrapper {
    flex-direction: column;
    gap: 10px;

    &.centered {
      max-width: 100%;
    }
  }

  .main-content {
    width: 100%;
    max-width: 100%;
  }

  .submit-btn {
    max-width: 300px;
    margin: 0 auto 20px auto;
  }

  .auth-container .content-wrapper .guest-section {
    flex: 0 0 auto !important;
    margin: 0 !important;
    padding: 16px 0 !important;
    min-height: auto !important;
    height: auto !important;
    width: 100% !important;
    max-width: 100% !important;
    justify-content: flex-start !important;
  }

  .guest-title {
    margin-bottom: 12px;
  }

  .guest-btn {
    margin-bottom: 12px;
  }

  .form-subtitle {
    padding: 0;
  }

  .form-title {
    font-size: 24px; // Smaller on mobile
  }
}
</style>
